import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import type { ITicketError } from '@/interface/ITicket';
import { BetSlipCtaAction } from '@/interface/enum';
import {
    resolveParlayFooterMessage,
    toSingleModeFooterMessage,
} from '@/libraries/betSlip/singleModeBetSlipErrorMessage';
import { useBetSlipCtaPresentation } from '@/libraries/composables/useBetSlipCtaPresentation';
import { useAccountStore } from '@/stores/accountStore';
import { useSingleModeBetSlipStore } from '@/stores/betSlip/singleModeBetSlipStore';

export function useParlayInSingleCta() {
    const store = useSingleModeBetSlipStore();
    const {
        parlayCtaTicketCount,
        parlayStakeError,
        parlayBalanceError,
        parlayMinBet,
        parlayMaxBet,
        parlayOpenTicketError,
        parlayCtaAction,
        isParlayCtaDisabled,
        hasIneligibleParlayTickets,
        hasConflictedParlayTickets,
    } = storeToRefs(store);
    const { handleParlayCtaClick } = store;
    const { customerInfo } = storeToRefs(useAccountStore());
    const { formatMoney } = useAccountStore();

    const parlayFooterError = computed<ITicketError | null>(() => {
        if (parlayOpenTicketError.value !== null) {
            return parlayOpenTicketError.value;
        }
        return resolveParlayFooterMessage({
            hasSameMatchConflict: hasConflictedParlayTickets.value,
            hasIneligibleParlayTickets: hasIneligibleParlayTickets.value,
            balanceError: parlayBalanceError.value,
            stakeError: parlayStakeError.value,
            parlayMinBet: parlayMinBet.value,
            parlayMaxBet: parlayMaxBet.value,
            hasUnacceptedOddsChange: parlayCtaAction.value === BetSlipCtaAction.AcceptChange,
            playerType: customerInfo.value.playerType,
        });
    });

    const parlayFooterErrorMessage = computed(() => (
        parlayFooterError.value === null
            ? null
            : toSingleModeFooterMessage(parlayFooterError.value, formatMoney)
    ));
    const {
        renderedCtaAction: renderedParlayCtaAction,
        isRenderedCtaDisabled: isRenderedParlayCtaDisabled,
        handleRenderedCtaClick: handleRenderedParlayCtaClick,
    } = useBetSlipCtaPresentation({
        ctaAction: parlayCtaAction,
        isCtaDisabled: isParlayCtaDisabled,
        handleCtaClick: handleParlayCtaClick,
    });

    return {
        parlayCtaTicketCount,
        parlayFooterErrorMessage,
        renderedParlayCtaAction,
        isRenderedParlayCtaDisabled,
        handleRenderedParlayCtaClick,
    };
}
